// <vita-buy-box> (blocks/vita-buy-box.liquid, P4-01): the one add to cart.
// Liquid renders every option, price and label; this only switches between them:
// - a rhythm or buy once radio changes the posted variant id and selling plan, the CTA label and price,
//   and the renewal line (hidden for buy once); fires vita:plan_selected {product_id, variant_id, plan,
//   price, discount_pct};
// - a size button (any product with a size option) fetches the block for that size through the section
//   rendering API, swaps it in and updates ?variant= (history.replaceState); fires vita:size_selected. It
//   keeps the mode: with a plan selected it opens the size's Routine plan (?variant=&selling_plan=), else
//   its Buy Once Single. Without JS it is a plain link;
// - the CTA adds through /cart/add.js with the drawer section, then resolves Horizon's cart event so the
//   cart drawer morphs and the header count updates; on success it fires vita:membership_cta with the
//   location (plan 'none' for buy once) and opens the cart drawer;
// - submit({ location }) lets the sticky bar and landing plan cards add the selected option;
//   select(plan) picks an option by plan ('3m', 'none'), for landing plan cards.

import { CartLinesUpdateEvent } from '@shopify/events';

const DRAWER_SECTION = 'vita-cart-drawer';

class VitaBuyBox extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.options = this.#json('[data-vita-options]', []);
    this.strings = this.#json('[data-vita-strings]', {});
    this.form = this.querySelector('[data-vita-buy-form]');
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.form?.addEventListener('submit', this.#onSubmit);
  }

  #json(selector, fallback) {
    try {
      return JSON.parse(this.querySelector(selector)?.textContent || '');
    } catch {
      return fallback;
    }
  }

  get radios() {
    return [...this.querySelectorAll('input[data-vita-option]')];
  }

  get selected() {
    const checked = this.radios.find((r) => r.checked);
    return this.options[Number(checked?.value ?? 0)];
  }

  select(plan) {
    const index = this.options.findIndex((o) => o.plan === plan);
    const radio = this.radios.find((r) => Number(r.value) === index);
    if (!radio || radio.checked) return;
    radio.checked = true;
    this.#changed();
  }

  submit({ location } = {}) {
    return this.#add(location || this.dataset.location || 'pdp');
  }

  #payload(option) {
    return {
      product_id: Number(this.dataset.productId),
      variant_id: option.variant_id,
      plan: option.plan,
      price: option.price / 100,
      discount_pct: option.discount_pct,
    };
  }

  #onChange = (e) => {
    if (e.target.matches('input[data-vita-option]')) this.#changed();
  };

  #changed() {
    const option = this.selected;
    if (!option) return;
    this.#render(option);
    window.vitaTrack?.('plan_selected', this.#payload(option));
    this.dispatchEvent(new CustomEvent('vita:buy-box-change', { bubbles: true, detail: option }));
  }

  #render(option) {
    const id = this.querySelector('[data-vita-id]');
    const plan = this.querySelector('[data-vita-plan]');
    const button = this.querySelector('[data-vita-cta-button]');
    const label = this.querySelector('[data-vita-cta-label]');
    const renewal = this.querySelector('[data-vita-renewal]');
    if (id) id.value = option.variant_id;
    if (plan) {
      plan.value = option.plan_id ?? '';
      plan.disabled = !option.plan_id;
    }
    const ship = this.querySelector('[data-vita-ship-perk]');
    if (ship) {
      const threshold = Number(ship.dataset.threshold);
      ship.textContent = threshold > 0 && option.price >= threshold ? ship.dataset.free : ship.dataset.member;
    }
    if (label) label.textContent = option.cta;
    if (button) button.disabled = !option.available;
    if (renewal) {
      renewal.textContent = option.renewal;
      renewal.hidden = !option.renewal;
    }
    this.querySelector('[data-vita-error]')?.setAttribute('hidden', '');
  }

  #onSubmit = (e) => {
    e.preventDefault();
    this.#add(this.dataset.location || 'pdp');
  };

  async #add(location) {
    const option = this.selected;
    if (this.#busy || !option?.available) return;
    this.#busy = true;

    const button = this.querySelector('[data-vita-cta-button]');
    const error = this.querySelector('[data-vita-error]');
    const status = this.querySelector('[data-vita-status]');
    button?.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    error?.setAttribute('hidden', '');
    if (status) status.textContent = this.strings.adding || '';

    const item = { id: option.variant_id, quantity: 1 };
    if (option.plan_id) item.selling_plan = option.plan_id;

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(option.variant_id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const root = window.Shopify?.routes?.root || '/';
      const sections = [
        ...new Set([
          DRAWER_SECTION,
          ...[...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId),
        ]),
      ].join(',');
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections, sections_url: window.location.pathname }),
      });
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.description || added.message || 'add to cart failed');

      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: 1,
          productId: this.dataset.productId,
          sections: added.sections,
          source: 'vita-buy-box',
          didError: false,
        },
      });
      if (status) status.textContent = this.strings.added || '';
      window.vitaTrack?.('membership_cta', { ...this.#payload(option), location });
      // the drawer opens after every buy box add, whatever Horizon's auto open setting says
      await deferred.promise.catch(() => {});
      document.querySelector('vita-cart-drawer')?.open?.(button);
    } catch (err) {
      deferred.reject(err);
      error?.removeAttribute('hidden');
      if (status) status.textContent = '';
    } finally {
      this.#busy = false;
      button?.removeAttribute('aria-busy');
      if (button) button.disabled = !this.selected?.available;
    }
  }

  // --- sizes (types B and C) ---
  #onClick = async (e) => {
    const link = e.target.closest('a[data-vita-size]');
    if (!link || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault();
    if (link.getAttribute('aria-current') === 'true') return;

    // keep the mode (P4-LOOP): with a plan selected, open that size's Routine plan, else its Buy Once
    const club = Boolean(this.selected?.plan_id) && link.dataset.clubHref;
    const url = new URL(club ? link.dataset.clubHref : link.dataset.onceHref || link.href, window.location.href);
    const variantId = Number(club ? link.dataset.clubVariantId : link.dataset.variantId);
    const fetchUrl = new URL(url);
    fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
    try {
      const html = await fetch(fetchUrl).then((r) => (r.ok ? r.text() : Promise.reject(new Error(String(r.status)))));
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector(`vita-buy-box[data-block-id="${CSS.escape(this.dataset.blockId)}"]`);
      if (!next) throw new Error('buy box not found');
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
      window.vitaTrack?.('size_selected', {
        product_id: Number(this.dataset.productId),
        variant_id: variantId,
        size: link.dataset.vitaSize,
        units: Number(link.dataset.units) || null,
        unit_price: Number(link.dataset.unitPrice) / 100 || null,
      });
      document.dispatchEvent(
        new CustomEvent('vita:variant-change', {
          detail: {
            productId: this.dataset.productId,
            variantId,
            mediaId: Number(link.dataset.mediaId) || null,
          },
        })
      );
      const size = link.dataset.vitaSize;
      this.replaceWith(next);
      next.querySelector(`a[data-vita-size="${CSS.escape(size)}"]`)?.focus();
    } catch {
      window.location.href = url.href;
    }
  };
}

if (!customElements.get('vita-buy-box')) customElements.define('vita-buy-box', VitaBuyBox);
